import type { ReactNode } from 'react';
import { cn } from '@/shared/cn';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import {
  GATE_APPROVED_SURFACE,
  GATE_GHOST_BUTTON,
  GATE_PRIMARY_BUTTON,
  gatePillClass,
} from '@/versions/v1/modules/advisory/gate-ui';

/** The one quiet pill (comp `.pill.p-n`): the neutral tone every landing's meta
 *  chips and gate record lines share. */
export const NEUTRAL_PILL =
  'inline-flex items-center whitespace-nowrap rounded-full bg-[var(--hairline)] px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em] text-ink-secondary';

/** One labelled field of a decision gate (comp `.fld`): caps label, then its value. */
export function DecisionField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-lg border border-[var(--hairline-strong)] bg-[var(--surface-1)] pl-2.5 pr-1.5 text-[12.5px] font-medium text-ink-primary">
      <span className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted">{label}</span>
      {children}
    </span>
  );
}

/**
 * The landings' `Record decision` gate (comp `.rgate`; the GM decision rail and
 * the Superintendent's Panel B and SMBS gates): the question line, the decision
 * field, the seats as Human actor chips, the date and the button. Once recorded
 * the gate eases to state-online, the decision locks and the record line shows.
 * A viewer who cannot decide sees the waiting label in place of the button.
 * Presentation only — the caller's transform supplies every string.
 */
export function DecisionGate({
  lead,
  text,
  options,
  value,
  onChoose,
  selectLabel,
  seats,
  date,
  recorded,
  canDecide,
  buttonLabel,
  buttonTone,
  onRecord,
  waitingLabel,
  recordLine,
  dataKey,
}: {
  /** The bold opening of the question line (`Decision needed:`). */
  lead: string;
  text: string;
  options: readonly string[];
  /** The choice shown in the decision field. */
  value: string;
  onChoose: (option: string) => void;
  /** Accessible name of the decision dropdown. */
  selectLabel: string;
  seats: readonly { role: string; holder: string }[];
  date: string;
  recorded: boolean;
  canDecide: boolean;
  buttonLabel: string;
  buttonTone: 'primary' | 'ghost';
  onRecord: () => void;
  waitingLabel: string;
  /** The stamped record and any confirmation still owed, once recorded. */
  recordLine?: { recorded: string; awaiting?: string };
  /** Data-attribute prefix: `data-<key>-gate`, `-waiting`, `-record`. */
  dataKey: string;
}) {
  // A recorded gate, or a persona who cannot decide, shows the choice without the dropdown's arrow.
  const locked = recorded || !canDecide;
  return (
    <div
      className={cn(
        'mt-2 flex flex-col gap-2 rounded-xl border py-2 pl-3 pr-2 transition-colors duration-300',
        recorded ? GATE_APPROVED_SURFACE : 'border-[var(--hairline-strong)] bg-[var(--surface-2)]',
      )}
      {...{ [`data-${dataKey}-gate`]: recorded ? 'recorded' : 'open' }}
    >
      <p className="text-[12.5px] text-ink-secondary">
        <span className="font-semibold text-ink-primary">{lead}</span> {text}
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        <DecisionField label="Decision">
          {options.length > 1 ? (
            <select
              aria-label={selectLabel}
              value={value}
              disabled={locked}
              onChange={(event) => onChoose(event.target.value)}
              className={cn(
                'appearance-none bg-transparent text-[12.5px] font-medium text-ink-primary outline-none',
                locked ? 'cursor-default pr-1' : 'cursor-pointer pr-3',
              )}
              style={
                locked
                  ? undefined
                  : {
                      backgroundImage:
                        'linear-gradient(45deg, transparent 50%, var(--ink-tertiary) 50%), linear-gradient(135deg, var(--ink-tertiary) 50%, transparent 50%)',
                      backgroundPosition: 'calc(100% - 5px) 55%, calc(100% - 1px) 55%',
                      backgroundSize: '4px 4px, 4px 4px',
                      backgroundRepeat: 'no-repeat',
                    }
              }
            >
              {options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <span className="pr-1">{options[0]}</span>
          )}
        </DecisionField>
        {seats.map((seat) => (
          <DecisionField key={seat.role} label={seat.role}>
            <ActorChip actor="Human">{seat.holder}</ActorChip>
          </DecisionField>
        ))}
        <DecisionField label="Date">
          <span className="pr-1 tabular-nums">{date}</span>
        </DecisionField>
        {recorded ? null : canDecide ? (
          <button
            type="button"
            className={cn(buttonTone === 'ghost' ? GATE_GHOST_BUTTON : GATE_PRIMARY_BUTTON, 'ml-auto h-7 px-3')}
            onClick={onRecord}
          >
            {buttonLabel}
          </button>
        ) : (
          <span className="ml-auto px-2 text-xs font-medium text-muted" {...{ [`data-${dataKey}-waiting`]: true }}>
            {waitingLabel}
          </span>
        )}
      </div>
      {recordLine ? (
        <div className="flex flex-wrap items-center gap-1.5" {...{ [`data-${dataKey}-record`]: true }}>
          <span className={gatePillClass('approved')}>{recordLine.recorded}</span>
          {recordLine.awaiting ? <span className={NEUTRAL_PILL}>{recordLine.awaiting}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
